import {Injectable} from '@angular/core';
import i18next from 'i18next';

import {
    ATTACHMENT_ICON_TEXT_GAP,
    ATTACHMENT_ICON_WIDTH,
    ARBEITSAGENTUR_LOGO_DATA_URI,
    JOBCENTER_LOGO_DATA_URI,
    COLUMN_GAP,
    FONT_SIZE_BODY,
    FONT_SIZE_HEADER_TITLE,
    FONT_SIZE_PAGINATION,
    FONT_SIZE_SECTION_TITLE,
    HEADER_LOGO_WIDTH,
    LABEL_WIDTH,
    PDF_LANG,
    PDF_LOGO_DATA_URI
} from './nachricht-pdf.constants';
import {NachrichtPdfUtilsService} from './nachricht-pdf-utils.service';
import {FilesizeTransformerService} from '../services/filesize.transformer.service';
import {AnhangMetadaten, Nachricht} from '../model/nachricht/nachricht';
import {HistorienElement} from '../model/historien-element';
import {getPdfDateTimeFormat2} from '../../shared/utils/date-time.helper';
import {NachrichtenTypeEnum} from '../model/nachricht/nachrichten-type.enum';

type PdfMessageFields = {
    datum: unknown;
    senderName: string;
    betreff: string;
    text: string;
};

@Injectable({
    providedIn: 'root'
})
export class NachrichtPdfBuilder {
    constructor(
        private readonly filesizeTransformer: FilesizeTransformerService,
        private readonly utils: NachrichtPdfUtilsService
    ) {}

    public build(doc: any, nachricht: Nachricht, filename: string): void {
        const documentRoot = doc.struct('Document', {
            title: filename,
            lang: PDF_LANG
        } as any);
        doc.addStructure(documentRoot);

        const outlineRoot = doc.outline.addItem(filename);

        this.writeHeader(doc, documentRoot, nachricht);
        this.writeCurrentMessageSection(doc, documentRoot, nachricht, outlineRoot);
        this.writeHistorySections(doc, documentRoot, nachricht, outlineRoot);
        this.writePageNumbers(doc);
    }

    private writeHeader(doc: any, parent: any, nachricht: Nachricht): void {
        const section = this.utils.createSection(doc, parent, 'Kopfbereich', PDF_LANG);
        const topY = doc.y;
        const leftX = doc.page.margins.left;
        const usableWidth = this.utils.getUsablePageWidth(doc) - 170;

        const headerLogo = nachricht.typ === NachrichtenTypeEnum.JC ? JOBCENTER_LOGO_DATA_URI : ARBEITSAGENTUR_LOGO_DATA_URI;

        try {
            const logoBuffer = this.utils.dataUriToArrayBuffer(headerLogo);
            this.utils.drawArtifactImage(doc, logoBuffer, this.utils.getHeaderLogoX(doc), topY, HEADER_LOGO_WIDTH);
        } catch (e) {
            console.warn('BA Logo konnte nicht gerendert werden:', e);
        }

        this.utils.ensurePageBreak(doc, 60);

        doc.font('BodyFont-Bold')
            .fontSize(FONT_SIZE_HEADER_TITLE)
            .text(nachricht.bezug ?? ' ', leftX, topY + 30, {
                width: usableWidth,
                structParent: section,
                structType: 'H'
            });

        doc.y = Math.max(doc.y, topY + 60);
        doc.moveDown(2);

        this.utils.ensurePageBreak(doc, 25);

        doc.font('BodyFont-Bold')
            .fontSize(FONT_SIZE_SECTION_TITLE)
            .text(this.getPdfHeaderText(nachricht), {
                structParent: section,
                structType: 'P'
            });

        doc.moveDown(0.5);
    }

    private writeCurrentMessageSection(
        doc: any,
        parent: any,
        nachricht: Nachricht,
        outlineParent: any
    ): void {
        const totalMessages = (nachricht.historie?.length ?? 0) + 1;
        const sectionTitle = `${this.t('nachricht.message')} ${totalMessages} ${this.t('download.of')} ${totalMessages}`;

        const section = this.utils.createSection(doc, parent, sectionTitle, PDF_LANG);
        outlineParent.addItem(sectionTitle);

        this.writeMessageFields(doc, section, this.mapNachrichtToMessageFields(nachricht));

        if (this.hasAttachments(nachricht)) {
            this.writeAttachments(doc, section, nachricht.anhaengeMetadaten ?? []);
        }

        doc.moveDown(1);
    }

    private writeHistorySections(
        doc: any,
        parent: any,
        nachricht: Nachricht,
        outlineParent: any
    ): void {
        const history = nachricht.historie ?? [];
        if (!history.length) {
            return;
        }

        const historyOutline = outlineParent.addItem(this.t('nachricht.history'));
        const totalMessages = history.length + 1;
        const betreff = nachricht.betreff ?? '';

        history.forEach((item: HistorienElement, index: number) => {
            const messageNumber = history.length - index;
            const title = `${this.t('nachricht.message')} ${messageNumber} ${this.t('download.of')} ${totalMessages}`;

            const section = this.utils.createSection(doc, parent, title, PDF_LANG);
            historyOutline.addItem(title);

            this.utils.ensurePageBreak(doc, 35);
            doc.font('BodyFont-Bold')
                .fontSize(FONT_SIZE_SECTION_TITLE)
                .text(title, doc.page.margins.left, doc.y, {
                    width: this.utils.getUsablePageWidth(doc),
                    structParent: section,
                    structType: 'H'
                });

            doc.moveDown(0.4);

            this.writeMessageFields(doc, section, this.mapHistorienElementToMessageFields(item, betreff));
            doc.moveDown(1);
        });
    }

    private writeMessageFields(doc: any, section: any, messageFields: PdfMessageFields): void {
        this.writeLabelValueParagraph(doc, section, `${this.t('nachricht.date')}:`, getPdfDateTimeFormat2(messageFields.datum as any));
        this.writeLabelValueParagraph(doc, section, `${this.t('nachricht.sender')}:`, messageFields.senderName);
        this.writeLabelValueParagraph(doc, section, `${this.t('nachricht.subject')}:`, messageFields.betreff);
        this.writeLabelValueParagraph(doc, section, `${this.t('nachricht.text')}:`, messageFields.text, true);
    }

    private writeLabelValueParagraph(
        doc: any,
        section: any,
        label: string,
        value: string,
        allowValuePageSplit: boolean = false
    ): void {
        const safeValue = (value ?? '').trim() || ' ';
        const layout = this.utils.getLabelValueLayout(doc);

        const labelHeight = this.utils.measureTextHeight(doc, 'BodyFont-Bold', FONT_SIZE_BODY, label, LABEL_WIDTH);
        const valueHeight = this.utils.measureTextHeight(doc, 'BodyFont', FONT_SIZE_BODY, safeValue, layout.valueWidth);
        const rowHeight = Math.max(labelHeight, valueHeight);

        if (allowValuePageSplit) {
            const firstLineHeight = this.utils.measureTextHeight(doc, 'BodyFont', FONT_SIZE_BODY, 'A', layout.valueWidth);
            const requiredStartHeight = Math.max(labelHeight, firstLineHeight);
            this.utils.ensurePageBreak(doc, requiredStartHeight + 10);
        } else {
            this.utils.ensurePageBreak(doc, rowHeight + 10);
        }

        const currentY = doc.y;

        doc.font('BodyFont-Bold')
            .fontSize(FONT_SIZE_BODY)
            .text(label, layout.startX, currentY, {
                width: LABEL_WIDTH,
                continued: false
            });

        doc.font('BodyFont')
            .fontSize(FONT_SIZE_BODY)
            .text(safeValue, layout.valueX, currentY, {
                width: layout.valueWidth,
                structParent: section,
                structType: 'P'
            });

        if (allowValuePageSplit) {
            doc.y = Math.max(doc.y, currentY + labelHeight) + 5;
            return;
        }

        doc.y = currentY + rowHeight + 5;
    }

    private writeAttachments(doc: any, section: any, attachments: AnhangMetadaten[]): void {
        this.utils.ensurePageBreak(doc, 40);

        doc.font('BodyFont-Bold')
            .fontSize(FONT_SIZE_BODY)
            .text(this.getAttachmentsLabel(attachments), doc.page.margins.left, doc.y, {
                width: LABEL_WIDTH,
                structParent: section,
                structType: 'P'
            });

        doc.moveDown(0.2);
        attachments.forEach(att => this.writeAttachmentItem(doc, section, att));
        doc.moveDown(0.6);
    }

    private writeAttachmentItem(doc: any, section: any, attachment: AnhangMetadaten): void {
        this.utils.ensurePageBreak(doc, 24);

        const currentY = doc.y;
        const iconX = doc.page.margins.left + LABEL_WIDTH + COLUMN_GAP;
        const textX = iconX + ATTACHMENT_ICON_WIDTH + ATTACHMENT_ICON_TEXT_GAP;
        const textWidth = doc.page.width - doc.page.margins.right - textX;
        const attachmentText = this.getAttachmentText(attachment);

        try {
            const pdfIconBuffer = this.utils.dataUriToArrayBuffer(PDF_LOGO_DATA_URI);
            this.utils.drawArtifactImage(doc, pdfIconBuffer, iconX, currentY - 18, ATTACHMENT_ICON_WIDTH);
        } catch (e) {
            console.warn('PDF Icon konnte nicht gerendert werden:', e);
        }

        doc.font('BodyFont')
            .fontSize(FONT_SIZE_BODY)
            .text(attachmentText, textX, currentY - 16, {
                width: textWidth,
                structParent: section,
                structType: 'P'
            });

        const neededTextHeight = doc.heightOfString(attachmentText, {width: textWidth});
        doc.y = currentY + neededTextHeight + 6;
    }

    private writePageNumbers(doc: any): void {
        const range = doc.bufferedPageRange();

        for (let i = range.start; i < range.start + range.count; i++) {
            doc.switchToPage(i);
            const text = `${i + 1} ${this.t('download.of')} ${range.count}`;

            doc.font('BodyFont').fontSize(FONT_SIZE_PAGINATION);
            const textWidth = doc.widthOfString(text);
            const x = doc.page.width - doc.page.margins.right - textWidth;
            const y = doc.page.height - doc.page.margins.bottom + 20;

            doc.markContent('Artifact', {
                type: 'Pagination',
                attached: ['Bottom', 'Right']
            } as any);

            doc.text(text, x, y, {lineBreak: false});
            doc.endMarkedContent();
        }
    }

    private mapNachrichtToMessageFields(nachricht: Nachricht): PdfMessageFields {
        return {
            datum: nachricht.datum,
            senderName: nachricht.senderName ?? '',
            betreff: nachricht.betreff ?? '',
            text: nachricht.text ?? ''
        };
    }

    private mapHistorienElementToMessageFields(el: HistorienElement, betreff: string): PdfMessageFields {
        return {
            datum: el.datum,
            senderName: el.senderName ?? '',
            betreff,
            text: el.text ?? ''
        };
    }

    private getAttachmentText(attachment: AnhangMetadaten): string {
        return `${(attachment.dateiname ?? '').trim()} (${this.filesizeTransformer.transform(attachment.groesse)})`;
    }

    private getAttachmentsLabel(attachments: AnhangMetadaten[]): string {
        return attachments.length > 1 ? `${this.t('nachricht.attachments')}:` : `${this.t('nachricht.attachment')}:`;
    }

    private getPdfHeaderText(nachricht: Nachricht): string {
        const total = (nachricht.historie?.length ?? 0) + 1;
        return `${this.t('download.newest')}: ${this.t('nachricht.message')} ${total} ${this.t('download.of')} ${total}`;
    }

    private hasAttachments(nachricht: Nachricht): boolean {
        return !!nachricht.anhaengeMetadaten?.length;
    }

    private t(key: string): string {
        return i18next.t(key);
    }
}